import { useState } from 'react';

import { Button, Input, Modal } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { validateNumberAndHyphenOnly, validateTelFormat } from '~/utils';

import { DEFAULT_INPUT_STATUS, ERROR_INVALID_FORMAT_INPUT_STATUS, ERROR_REQUIRED_INPUT_STATUS } from '~/constants';

import type { TypeInputState } from '~/types/common.types';
import type { TypePickupAddress } from '~/types/receive.types';

import styles from './ReceivePickupAddressModal.module.scss';

type TypePickupAddressStatus = {
  zipCode: TypeInputState;
  address: TypeInputState;
  contactNumber: TypeInputState;
};

type TypeReceivePickupAddressModalProps = {
  mode: 'register' | 'detail';
  data: TypePickupAddress;
  open: boolean;
  onCancel: () => void;
  onConfirm: (data: TypePickupAddress) => void;
};

/**
 * 출고지 정보 등록/수정 모달
 */
const ReceivePickupAddressModal = ({ mode, data, open, onCancel, onConfirm }: TypeReceivePickupAddressModalProps) => {
  const [value, setValue] = useState<TypePickupAddress>(data);

  const [valueStatus, setValueStatus] = useState<TypePickupAddressStatus>({
    zipCode: DEFAULT_INPUT_STATUS,
    address: DEFAULT_INPUT_STATUS,
    contactNumber: DEFAULT_INPUT_STATUS,
  });

  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setValue((prev) => ({ ...prev, zipCode, address }));
    },
  });

  const validateForm = (): boolean => {
    const updatedValueStatus = { ...valueStatus };

    if (value.zipCode === '') {
      updatedValueStatus.zipCode = ERROR_REQUIRED_INPUT_STATUS;
    }
    if (value.address === '') {
      updatedValueStatus.address = ERROR_REQUIRED_INPUT_STATUS;
    }
    if (value.contactNumber === '') {
      updatedValueStatus.contactNumber = ERROR_REQUIRED_INPUT_STATUS;
    } else if (!validateTelFormat(value.contactNumber)) {
      updatedValueStatus.contactNumber = ERROR_INVALID_FORMAT_INPUT_STATUS;
    }

    setValueStatus(updatedValueStatus);

    const isValid = Object.values(updatedValueStatus).every((status) => status.status !== 'error');

    return isValid;
  };

  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    onConfirm(value);
  };

  return (
    <Modal
      open={open}
      title={`출고지 정보 ${mode === 'register' ? '등록' : '수정'}`}
      size="small"
      confirmText={mode === 'register' ? '등록' : '저장'}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <div>
        <FormField
          label="우편번호"
          required
          contents={
            <>
              <Input
                type="text"
                size="small"
                value={value.zipCode}
                disabled
                status={valueStatus.zipCode.status}
                validationMessage={valueStatus.zipCode.message}
              />
              <Button
                size="small"
                color="secondary"
                onClick={() => {
                  handleZipCodeSearchOpen();
                  setValueStatus({ ...valueStatus, zipCode: DEFAULT_INPUT_STATUS, address: DEFAULT_INPUT_STATUS });
                }}
              >
                검색
              </Button>
            </>
          }
          labelClassName={styles['label']}
          contentsClassName={styles['inputs-wrapper']}
        />
        <FormField
          label="기본주소"
          required
          contents={
            <Input
              type="text"
              size="small"
              value={value.address}
              disabled
              status={valueStatus.address.status}
              validationMessage={valueStatus.address.message}
            />
          }
          labelClassName={styles['label']}
        />
        <FormField
          label="상세주소"
          contents={
            <Input
              type="text"
              size="small"
              placeholder="입력"
              value={value.detailAddress}
              onChange={(e) => setValue({ ...value, detailAddress: e.target.value })}
              onBlur={(e) => setValue({ ...value, detailAddress: e.target.value.trim() })}
            />
          }
          labelClassName={styles['label']}
        />
        <FormField
          label="연락처"
          required
          contents={
            <Input
              type="text"
              size="small"
              placeholder="- 포함 입력"
              status={valueStatus.contactNumber.status}
              validationMessage={valueStatus.contactNumber.message}
              value={value.contactNumber}
              onChange={(e) => {
                const newValue = e.target.value;
                if (validateNumberAndHyphenOnly(newValue)) {
                  setValue({ ...value, contactNumber: newValue });
                }
              }}
              onBlur={(e) => setValue({ ...value, contactNumber: e.target.value.trim() })}
              onFocus={() => setValueStatus({ ...valueStatus, contactNumber: DEFAULT_INPUT_STATUS })}
            />
          }
          labelClassName={styles['label']}
        />
      </div>
    </Modal>
  );
};

export default ReceivePickupAddressModal;
